// various helpers for QuoteSheetReply components
import Vue from 'vue';

//import { v4 as uuidv4 } from 'uuid';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import { 
    vendorColsAll,
    genericBidPriceFormatter,
} from './quote-sheet-vendor-cols';
import { LinkedProduct } from './quote-sheet-common-helpers';
import { 
    toNumberOrNull, 
    hasValueOrZero, 
    random22id, 
    //FormatCurrencyHelper, 
    HumanizeDurationFromIsoHelper, 
    DurationIsoToMinutes 
} from '../../js/filters-helpers';


// CLASSES


class QsItemForReply {
    constructor({ // don't set default values for most things, unless it really makes sense AND they are optional on front-end
        id = '',  // DO NOT SET ON FRONTEND, but keep it if it was already set on incoming data from backend
        name = '',  // optional, but only if we have linked_product, otherwise it should be provided
        description = '',  // optional
        guid,  // guid as used on front-end, required because we use this as 'placeholder' for FK from variants
        sort_order = null,  // sort order in overall list, (one-based, NOT zero-based), not used until we output in API format
        part_number = '', // ONLY USE IF WE DON'T HAVE LINKED_PRODUCT
        linked_product = null,  // may be multiple things... we'll use this to set linked_product_id and linked_product_obj - nested object of any linked_product, only required if it has a linked_product, will get used in this.linked_product and this.linked_product_id (for writes) - keep default here as null, then set to object below if needed
        quotesheet_id = '',  // may set this in backend instead... just set it when reconstructing to send back to API???  or not needed on frontend at all? (sounds best)
    }) {
        this.id = id;
        this.name = name;
        this.description = description;
        this.guid = guid;
        this.sort_order = toNumberOrNull(sort_order);
        this.part_number = part_number;
        // DEAL WITH linked_product -- set linked_product_obj AND linked_product_id - this used to be in separate method, now pulling up to constructor since you won't need to call it separately for reply-related things
        if (!!linked_product && typeof linked_product === 'string') {  // if you passed in the linked_product_id, set it as {id: 'the_string'}
            this.linked_product_obj = {id: linked_product};
            this.linked_product_id = linked_product;
        } else if (!!linked_product && isObject(linked_product) && !isEmpty(linked_product)) {  
            this.linked_product_obj = new LinkedProduct(linked_product); 
            this.linked_product_id = this.linked_product_obj.id || '';  // default to empty string in case you passed empty object
        } else {
            this.linked_product_obj = {};  // our 'default' -- should be an empty object so we don't throw errors looking for things in it
            this.linked_product_id = '';
        }
        // FREEZE linked_product_obj, no matter what it is, since we won't change linked products from here
        Object.freeze(this.linked_product_obj);
        // 
        //this.setLinkedProduct(linked_product);  // moved this all into the constructor itself
        this.quotesheet_id = quotesheet_id;
    }
    // GETTERS
    /* get linked_product_id() {
        return this.linked_product.id || '';
    } */
    get hasLinkedProduct() {
        let lprod = this.linked_product_obj;  // might be empty object
        return !!lprod && !!lprod.id;  // if it has name or ID -- is that good?
    }
    get itemName() {  // dynamically return what we should use for main name -- either from this.name or this.linked_product_obj.name
        // dynamically return the name based on whether has linked_product or not
        if (this.hasLinkedProduct) {  // check if we have linked_product, with name or ID
            return this.linked_product_obj.name;
        } else {  
            return this.name || '';  // return empty string as default in case we didn't set name somehow
        }
    }
    get partNumber() {
        // return the correct value for part_number, either from this.part_number or this.linked_product_obj.part_number
        // we will not allow overriding existing part_number inside linked_product_obj... but can get from there
        if (this.hasLinkedProduct) {
            return this.linked_product_obj.part_number;
        } else {
            return this.part_number || '';  // return empty string as default
        }
    }
    // METHODS - no longer needed for reply-related things... you previously used these classes for reply and for builder
    /* setLinkedProduct(linked_product) {
        // use this to reassign all things related to the linked_product
        if (!!linked_product && typeof linked_product === 'string') {  // if you passed in the linked_product_id, set it as {id: 'the_string'}
            this.linked_product_obj = {id: linked_product};
            this.linked_product_id = linked_product;
        } else if (!!linked_product && isObject(linked_product) && !isEmpty(linked_product)) {  
            this.linked_product_obj = new LinkedProduct(linked_product); 
            this.linked_product_id = this.linked_product_obj.id || '';  // default to empty string in case you passed empty object
        } else {
            this.linked_product_obj = {};  // our 'default' -- should be an empty object so we don't throw errors looking for things in it
            this.linked_product_id = '';
        }
        // FREEZE linked_product_obj, no matter what it is, since we won't change linked products from here
        Object.freeze(this.linked_product_obj);
    } */
    /* formatForApi(sortOrder) {  // return version of itself in format ready to POST to API
        // pass in sortOrder, then 1-based position of this object in the overall list (since this object doesn't know its position in overall list)
        return {  // this makes a new object, so no need to clone in caller
            id: this.id,
            name: this.name,
            description: this.description,
            guid: this.guid,
            sort_order: sortOrder,
            part_number: this.part_number,
            linked_product_id: this.linked_product_id,
        }
    }
    clone() {
        // clone this object, resetting certain things as needed
        let cloneArgs = {  // make sure this matches the object args in constructor!
            id: '',  // must reset this when cloning
            name: this.name, 
            description: this.description,
            guid: uuidv4(),  // must set a new guid when cloning
            sort_order: null,  // probably doesn't matter what you set to when cloning... 
            linked_product: this.linked_product_obj,  // nested object of any linked_product, only required if it has a linked_product, will get used in this.linked_product and this.linked_product_id (for writes) - keep default here as null, then set to object below if needed
            quotesheet_id: this.quotesheet_id,
        };
        return new QsItemForReply(cloneArgs);
    }
    isValid() {
        let requiredFields = ['guid'];
        if (!this.hasLinkedProduct) {
            requiredFields.push('name');  // make sure we have a name if we don't have linked_product
        }
        //console.log(`From isValid on QsItemForReply guid ${this.guid} - required fields below:`);
        //console.log(requiredFields);
        let requiredFieldsOk = requiredFields.every(fld => hasValueOrZero(this[fld]));
        let linkedProductValid = true;  // default... check if we have one
        if (this.hasLinkedProduct) {
            linkedProductValid = this.linked_product_obj.isValid();
        }
        return requiredFieldsOk && linkedProductValid;
    } */
}

class QsItemVariantForReply {
    constructor({
        id = '',  // DO NOT SET ON FRONTEND, but keep it if it was already set on incoming data from backend
        description = '',  // optional
        guid,  // guid as used on front-end, required because we use this as 'placeholder' for FK from variants
        sort_order = null,  // sort order relative to PARENT, (one-based, NOT zero-based), only used when we output in API format
        quantity = null,
        uom = '',
        quantity_timeframe = '',
        need_by_date = null,
        target_price = null,
        target_price_currency = '',
        current_price = null,
        current_price_currency = '',
        enable_price = true,
        enable_subtotal = false,
        enable_quoted_price = false,
        enable_quoted_subtotal = false,
        enable_comment = true,
        enable_moq = true,
        enable_leadtime = true,
        enable_deliverydate = true,
        enable_country_of_origin = true,
        enable_shipterms = true,
        enable_payterms = true,
        enable_nobid = true,
        // qs_item_guid = '',  // removing this because we'll have it in VariantRowData object as parent_guid, so don't keep multiple places - but make sure to set it on Row level when preparing data for API
    }) {
        this.id = id;
        this.description = description;
        this.guid = guid;
        this.sort_order = toNumberOrNull(sort_order);
        this.quantity = toNumberOrNull(quantity);
        this.uom = uom;
        this.quantity_timeframe = quantity_timeframe;
        this.need_by_date = need_by_date;  // TODO - format to Luxon date?
        this.target_price = toNumberOrNull(target_price);
        this.target_price_currency = target_price_currency;
        this.current_price = toNumberOrNull(current_price);
        this.current_price_currency = current_price_currency;
        //this.qs_item_guid = qs_item_guid;
        // vendor col fields -- Boolean versions
        this.enable_price = enable_price;
        this.enable_subtotal = enable_subtotal;
        this.enable_quoted_price = enable_quoted_price;
        this.enable_quoted_subtotal = enable_quoted_subtotal;
        this.enable_comment = enable_comment;
        this.enable_moq = enable_moq;
        this.enable_leadtime = enable_leadtime;
        this.enable_deliverydate = enable_deliverydate;
        this.enable_country_of_origin = enable_country_of_origin;
        this.enable_shipterms = enable_shipterms;
        this.enable_payterms = enable_payterms;
        this.enable_nobid = enable_nobid;
    }
    // GETTERS
    // METHODS - no longer needed for reply-related things... you previously used these classes for reply and for builder
    /* formatForApi(sortOrder) {  // return version of itself in format ready to POST to API
        // pass in sortOrder, then 1-based position of this object under its PARENT (relative to siblings) (since this object doesn't know its own position)
        // return a new object so no need to clone in caller 
        let apiObj = {...this}; // make shallow copy - this is what we'll return
        apiObj.sort_order = sortOrder;  // set 1-based sort_order
        return apiObj;
    }
    clone() {
        // clone this object, resetting certain things as needed
        // a bit of a hack, but we can just run through formatForApi(dummy_sort_order), reset some things, then pass that to class constructor
        // doing that to make sure we have all params in correct format/type
        let cloneArgs = this.formatForApi(null);
        cloneArgs.id = '';  // blank this since its not saved to backend yet
        cloneArgs.guid = uuidv4();  // assign new guid
        return new QsItemVariantForReply(cloneArgs);
    }
    isValid() {
        let requiredFields = ['guid'];
        let requiredFieldsOk = requiredFields.every(fld => hasValueOrZero(this[fld]));
        //let vendorColsAtLeastOne = this.allEnableKeys.some(enableKey => this[enableKey]);  // requires these to be real Booleans
        let vendorColsAtLeastOne = vendorColsAll.some(vcol => this[vcol.enableKey]);  // uses canonical vendorColsAll from this file - these are booleans, so no need for hasValueOrZero()
        return requiredFieldsOk && vendorColsAtLeastOne;
    } */
}



class QuoteSheetReplyBid {  // single line-item BID on a QuoteSheetReply
    // this should AT LEAST match all the params of your backend class so you can use it to build exact object as needed by backend to send back to API
    constructor({  // make sure there are defaults for everything... we'll instantiate empty one before we get real one from API
        id = random22id(),  // random22id -- matches our id generator on backend
        price = null,  // money field amount
        price_currency = '',  // money field currency --- PASS THIS IN, EVEN IF INSTANTIATING WITH NEW DATA - get from quoteSheetData
        // NOT including subtotal here -- that's purely calculated on frontend and backend based on price/quantity
        // CHANGED Sept 1 2022 - now including subtotal - calculated on backend (@property) then output and used here for display in COMPARE GRID
        // still won't have it for reply grid (this is used there too) - since we need to calculate it live on front-end when vendor is inputting it
        subtotal = null,  // will be string decimal amount - "1.0000" - must use currency from 'price_currency' - OR will be a normal 
        // NEW JUNE 1 2023 - quoted_price and friends -- the actual price being quoted by supplier - price becomes calculated based on exchange rate
        quoted_price = null,
        quoted_price_currency = '',
        quoted_subtotal = null,  // see above
        // 
        moq = null,  // decimal field
        leadtime = '',  // duration field ?  or ISO duration text?
        leadtime_iso = '',  // ISO duration text
        deliverydate = null,  // DateField
        country_of_origin = '',  // 2-letter country code
        shipterms = '',
        payterms = '',
        comment = '',
        nobid = false,
        qs_item_variant_id = '',  // You WILL have this in incoming data - just the ID string, not full object
        //quotesheet_reply = {},  // do you need this?  maybe just set on the way out before sending back to API?
    } = {}) {
        this.id = id;  // string ID - should we move random22id() here?  Maybe... if we want to put more logic around validating existing IDs.
        this.price = toNumberOrNull(price);  // incoming as a STRING -- dj-money does that for correct decimal representation ??
        this.price_currency = price_currency;  // 3-char string ('USD')
        this.subtotal = toNumberOrNull(subtotal);  //// NOT USED CLIENT-SIDE?  you may be calculating it only where/when needed -- incoming as a string decimal amount OR number - "1.0000"
        this.quoted_price = toNumberOrNull(quoted_price);
        this.quoted_price_currency = quoted_price_currency;
        this.quoted_subtotal = toNumberOrNull(quoted_subtotal);  // NOT USED CLIENT-SIDE?  you may be calculating it only where/when needed
        this.moq = toNumberOrNull(moq);  // number - Decimal field on backend
        this.leadtime = leadtime_iso;  // type??? FIX THIS <--------------------------------------------------- FIX THIS
        this.leadtime_from_api = leadtime;  // just a place to hold this for now
        this.leadtime_iso = leadtime_iso;  // ISO 8601 string
        this.deliverydate = deliverydate;   // should we use Date objects here?  Would affect editor/renderer components, formatter, etc... but maybe best?
        this.country_of_origin = country_of_origin;  // 2-letter country code
        this.shipterms = shipterms;  // string
        this.payterms = payterms;  // string
        this.comment = comment;  // string
        this.nobid = nobid;  // boolean
        this.qs_item_variant_id = qs_item_variant_id;  // string ID
        //this.quotesheet_reply = quotesheet_reply;  // ?
    }
    // GETTERS
    get hasValidPrice() {
        // we check this in some places before getting price, for filtering out bids without a valid price
        return Number.isFinite(this.price);  // will be false if this.price is null - checks that its a Number, and not +/- Infinity, not NaN
    }
    get hasValidQuotedPrice() { 
        return Number.isFinite(this.quoted_price);
    }
    // this works for showing formatted price, but keep in mind it will be a string, not a number, so can't be used for sorting, etc.
    get priceFormatted() {  // added this to be used in compareGrid -- maybe can use in replyGrid also, but need to test
        // OLD return FormatCurrencyHelper(this.price, this.price_currency);  // this handles not rejecting zero, will return null if price is null/undefined
        return genericBidPriceFormatter(this.price, this.price_currency);
    }
    get quotedPriceFormatted() {  // added this to be used in compareGrid -- maybe can use in replyGrid also, but need to test
        // OLD return FormatCurrencyHelper(this.price, this.price_currency);  // this handles not rejecting zero, will return null if price is null/undefined
        return genericBidPriceFormatter(this.quoted_price, this.quoted_price_currency);
    }
    get leadtimeFormatted() {  // added this to be used in compareGrid - maybe can use in replyGrid also, but need to test
        return HumanizeDurationFromIsoHelper(this.leadtime_iso);
    }
    get leadtimeMinutes() {
        // returns the duration as a NUMBER of minutes so you can use for sorting, etc.
        // returns null if no value, or if not valid
        return DurationIsoToMinutes(this.leadtime_iso);
    }
    /* NOT USING THIS - as of March 4 2022
    get priceFormatterFunction() {
        // return the FUNCTION to format the price, not the actual formatted price
        // must still pass a value to this (currency will be baked in) -- do that when/where you use this
        // why?  so we can use in valueFormatter in ag-grid, but still return the raw numeric value in valueGetter, which is needed for sorting, etc.
        // DECENT IDEA, but probably not using it... you'd have to do some gymnastics to get to this anyways, might as well just get currency field directly
        const priceFormatter = (value) => FormatCurrencyHelper(value, this.price_currency);
        return priceFormatter;
    } */
    // SETTERS
    // METHODS
    formatForApi() { // pass in any args for other things that you need to set or check to build this
        let apiObj = {...this}; // make shallow copy - this is what we'll return
        // TODO - remove price?  if we only use quoted_price and calculate price from base currency on backend?
        // do any other transforms...
        // TODO - if there is no price, should you remove price_currency?  see how backend handles it
        // TODO - formatting of leadtime -- ISO?  something else?
        // NEW - March 4 2022 - remove price (and currency?) if nobid=True??? should not show to internals if they nobid
        // we will not remove price when user clicks on nobid... only when they're ready to submit it (in here)
        if (apiObj.nobid) {
            apiObj.price = null;  // we should ignore this server side anyways
            apiObj.quoted_price = null;
            apiObj.quoted_price_currency = '';  // or None?
            // just leave currency?  make sure that doesn't cause problems in your currency checks...
            // what about other cols?  remove those too? if so, make a 'setEmtpyValue()' function for each col/field
        }
        apiObj.leadtime_iso = this.leadtime;  // SHORT-TERM SOLUTION - make sure it matches constructor logic
        apiObj.leadtime = null;  
        return apiObj;
    }
    isValid() {
        // we are checking required fields in 'parent' BidGridRow, since we have access to variant there too
        // do field-level validation here -- like making sure numbers don't have too many digits, etc.
        // check that dates, things like that are all formatted right
        return true;
    }
}


class BidGridRow {  // THE ACTUAL DATA OBJECT IN THE QuoteSheetReply ag-grid rowNode.data is this object
    constructor({
        bid = new QuoteSheetReplyBid({}),  // pass in instance of QuoteSheetReplyBid, or null if its new and we'll make a new one in constructor()
        item,  // pass in instance of QsItemForReply -- linked_product will be nested inside
        variant,  // pass in instance of QsItemVariantForReply
        exchangeRate,  // if you have selected quoted_currency already, you should have this... but not if not yet selected
    }) {
        this.rowId = bid.id;  // then use gridOptions.getRowNodeId() to use this as the ID in the grid too
        this.groupId = item.id;  // bring this to top level to make it clear how grouping happens
        this.bid = bid;  // pass in QuoteSheetReplyBid object instance
        this.item = Object.freeze(item);  // instance of QsItemForReply - freeze it to prevent modifications - linked_product will be nested inside if any
        this.variant = Object.freeze(variant);  // instance of QsItemVariantForReply - freeze it to prevent modifications
        this.exchangeRate = exchangeRate;
    }
    // GETTERS
    get itemName() {
        return this.item.itemName;  // uses getter inside nested item, gets from item or from linked_product as appropriate
    }
    get partNumber() {
        return this.item.partNumber;  // uses getter inside nested item, gets from item or from linked_product as appropriate
    }
    get enabledVendorColObjs() { // return an array of full vendorCol objects (from canonical array) -- only the ones that are turned on for THIS SPECIFIC ROW
        // loop through canonical vendorColsAll, see which ones are enabled in this variant
        return vendorColsAll.filter(vcol => this.variant[vcol.enableKey]);
    }
    get enabledVendorColKeys() { // return an array of plainKey vendor cols -- only the ones that are turned on for THIS SPECIFIC ROW
        // just do a .map() from this.enabledVendorColObjs -- DRY
        return this.enabledVendorColObjs.map(vcol => vcol.plainKey);  // Array of plainKeys ['price', 'leadtime', ...]
    }
    get requiredVendorColKeys() {  // OF THE ENABLED vendorCols for THIS LINE, which ones are REQUIRED (used for validation of THIS LINE)
        // uses and returns array of plainKey strings such as ['price', 'leadtime', ...]
        const reqKeys = this.enabledVendorColObjs.filter(vcol => vcol.required).map(vcol => vcol.plainKey);
        // OMIT 'price' -- calculated on backend!
        return reqKeys.filter(rk => rk !== 'price');
    }
    get isValidStatus() {
        return this.isValid() ? 'valid' : 'invalid';  // just a shortcut to making isValid a property for easier use of lodash countBy function
    }
    get calculatedSubtotal() {  // JUST THE AMOUNT, NOT FORMATTED WITH CURRENCY 
        // QuoteSheetReplyBid has 'subtotal' attribute, but its used in the Compare grid - not in reply grid, where we calculate it live using this getter
        // NOTE - price (which this is based on) is likely STALE in the bid -- it is not calculated live based on vendor editing 'quoted_price' and exchange rate
        // so we must get the converted price as calculated here on front-end
        if (!this.variant.enable_subtotal || this.bid.nobid) {
            return null;
        }
        const livePriceAmount = this.getConvertedPriceAmount();
        return hasValueOrZero(livePriceAmount) ? ((this.variant.quantity ?? 1) * livePriceAmount) : null;
    }
    get calculatedQuotedSubtotal() {  // JUST THE AMOUNT, NOT FORMATTED WITH CURRENCY 
        // QuoteSheetReplyBid has 'quoted_subtotal' attribute, but its used in the Compare grid - not in reply grid, where we calculate it live using this getter
        // since vendor actually enters 'quoted_price', we just use that value, don't have to worry about stale values from backend like on 'price'
        if (!this.variant.enable_quoted_subtotal || this.bid.nobid) {
            return null;
        }
        return hasValueOrZero(this.bid.quoted_price) ? ((this.variant.quantity ?? 1) * this.bid.quoted_price) : null;
    }
    // for description and variantDescription, just use valueGetter in ag-grid colDefs
    // SETTERS
    // METHODS
    formatForApi(quoteSheetReplyId) {
        // output in format needed for API -- pass in the QuoteSheetReply instance so you can get stuff from it
        let apiObj = this.bid.formatForApi();  // returns shallow copy of this.bid (QuoteSheetReplyBid instance)
        apiObj['qs_item_variant_id'] = this.variant.id;  // bulk update serializer expects 'qs_item_variant_id'
        apiObj['quotesheet_reply'] = quoteSheetReplyId;  // you ONLY need the ID so just pass that as arg - MAY NOT NEED THIS - CAN SET IT IN SERIALIZER
        return apiObj;
    }
    isValid() {
        // do 'overall' validation here -- do we have all the required things?
        // do 'field level' validation in the bid object itself
        let hasAllRequiredVendorCols = this.requiredVendorColKeys.every(plainKey => hasValueOrZero(this.bid[plainKey]));  // did vendor fill out all required vendorCols?
        let selectedNoBid = this.bid.nobid;  // did vendor select nobid?  If so, we don't need them all selected
        let bidValid = this.bid.isValid();  // is the bid itself valid using its own isValid() ?
        return (hasAllRequiredVendorCols || selectedNoBid) && bidValid;
    }
    getConvertedPriceAmount() {
        // helper to call from cellRenderer that outputs the converted 'price' from the 'quoted_price' (as a Number, or null if not possible)
        // THIS DOES CONVERSION HERE ON FRONT-END, using exchangeRate from backend
        // you might not want to use this... maybe only use converted values from backend?
        // target = base * rate   (target = the price, base = quoted_price)
        if (!this.exchangeRate || !this.bid.price_currency || !hasValueOrZero(this.bid.quoted_price)) {
            return null;
        }
        return this.bid.quoted_price * this.exchangeRate;
    }
    getConvertedPriceFormatted() {
        // helper to call from cellRenderer that outputs the formatted 'price' from the 'quoted_price' - or '' if no valid exchangeRate
        // THIS DOES CONVERSION HERE ON FRONT-END, using exchangeRate from backend
        // you might not want to use this... maybe only use converted values from backend?
        // target = base * rate   (target = the price, base = quoted_price)
        // TODO - inspect the number of decimal places of quoted_price, output the same number... if possible
        const priceAmount = this.getConvertedPriceAmount();
        return hasValueOrZero(priceAmount) ? genericBidPriceFormatter(priceAmount, this.bid.price_currency) : '';
    }
}


const StatusBarTotalsComponent = Vue.extend({
    data: function() {
        return {}
    },
    computed: {
        grandTotal() {
            const totalVal = this.params.context.componentParent.grandTotal;
            return hasValueOrZero(totalVal) ? genericBidPriceFormatter(totalVal, this.params.currency) : '';
        },
        quotedGrandTotal() {
            const totalVal = this.params.context.componentParent.quotedGrandTotal;
            return hasValueOrZero(totalVal) ? genericBidPriceFormatter(totalVal, this.params.quoted_currency) : '';
        },
        showBoth() {
            // we only show the quotedGrandTotal, unless currencies are different, then we show both (quotedGrandTotal more prominent, that's what they're quoting in)
            return this.params.quoted_currency !== this.params.currency;
        },
    },
    template: `
        <div class="font-weight-bold py-2 font-size-100">
            <span class="text-primary-darker">QUOTE TOTAL:</span>
            <span class="">{{ quotedGrandTotal }}</span>
            <span v-if="showBoth" class="font-weight-normal text-muted ml-3">({{ params.currency}} {{ grandTotal }})</span>
        </div>
    `
});


export {
    QsItemForReply,
    QsItemVariantForReply,
    QuoteSheetReplyBid,
    BidGridRow,
    StatusBarTotalsComponent,
}